import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';
import { FAMILY_TOKENS, VALID_TOKENS, getNameForToken } from './tokens';

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    background: '#1a1a2e',
    color: '#e8e8f0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    maxWidth: 680,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 16px',
    background: '#16213e',
    borderBottom: '1px solid #0f3460',
    flexShrink: 0,
  },
  headerTitle: { fontSize: 18, fontWeight: 700, color: '#e94560' },
  headerSub: { fontSize: 12, color: '#8888aa', marginTop: 1 },
  online: {
    marginLeft: 'auto',
    fontSize: 11,
    color: '#4ade80',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#4ade80',
    animation: 'pulse 2s infinite',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 12px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  bubble: (isMine) => ({
    maxWidth: '75%',
    alignSelf: isMine ? 'flex-end' : 'flex-start',
    background: isMine ? '#e94560' : '#16213e',
    borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    padding: '10px 14px',
    wordBreak: 'break-word',
  }),
  bubbleName: (isMine) => ({
    fontSize: 11,
    fontWeight: 600,
    color: isMine ? 'rgba(255,255,255,0.7)' : '#8888cc',
    marginBottom: 4,
  }),
  bubbleText: { fontSize: 15, lineHeight: 1.4 },
  bubbleTime: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.45)',
    marginTop: 4,
    textAlign: 'right',
  },
  bubbleImg: {
    maxWidth: '100%',
    borderRadius: 10,
    marginBottom: 4,
    display: 'block',
  },
  inputBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    padding: '10px 12px',
    background: '#16213e',
    borderTop: '1px solid #0f3460',
    flexShrink: 0,
  },
  textarea: {
    flex: 1,
    background: '#0f3460',
    border: 'none',
    borderRadius: 20,
    padding: '10px 14px',
    color: '#e8e8f0',
    fontSize: 15,
    resize: 'none',
    outline: 'none',
    lineHeight: 1.4,
    maxHeight: 120,
    overflowY: 'auto',
    fontFamily: 'inherit',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 6,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8888cc',
    flexShrink: 0,
  },
  sendBtn: (active) => ({
    background: active ? '#e94560' : '#0f3460',
    border: 'none',
    cursor: active ? 'pointer' : 'default',
    padding: '10px 16px',
    borderRadius: 20,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    flexShrink: 0,
    transition: 'background 0.2s',
  }),
  denied: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100dvh',
    gap: 16,
    padding: 24,
    textAlign: 'center',
    background: '#1a1a2e',
    color: '#e8e8f0',
  },
  uploadLabel: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 6,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8888cc',
    flexShrink: 0,
  },
  uploading: {
    fontSize: 12,
    color: '#8888cc',
    padding: '4px 8px',
    alignSelf: 'flex-end',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function autoGrow(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  // Short path aliases: /j = Jordan, /c = Chrislynn, /k = Cain
  const PATH_ALIASES = { '/j': FAMILY_TOKENS.jordan, '/c': FAMILY_TOKENS.chrislynn, '/k': FAMILY_TOKENS.cain };
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const token = PATH_ALIASES[path] || params.get('token') || '';

  if (!VALID_TOKENS.has(token)) {
    return (
      <div style={S.denied}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Access Denied</div>
        <div style={{ fontSize: 14, color: '#8888aa', maxWidth: 280 }}>
          Open the link Jordan sent you — it has your personal token in it.
        </div>
      </div>
    );
  }

  return <Chat token={token} />;
}

function Chat({ token }) {
  const myName = getNameForToken(token);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [connected, setConnected] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const fileRef = useRef(null);

  // ── Load history ──────────────────────────────────────────────────────────
  useEffect(() => {
    supabase
      .from('family_chat')
      .select('*')
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (data) setMessages(data);
      });
  }, []);

  // ── Realtime subscription ─────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel('family-chat-room')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'family_chat' },
        (payload) => {
          setMessages((prev) => {
            // Avoid duplicates if we already added optimistically
            if (prev.find((m) => m.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
        }
      )
      .subscribe((status) => {
        setConnected(status === 'SUBSCRIBED');
      });

    return () => supabase.removeChannel(channel);
  }, []);

  // ── Scroll to bottom ──────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send text message ─────────────────────────────────────────────────────
  const sendText = useCallback(async () => {
    const msg = text.trim();
    if (!msg) return;
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const { error } = await supabase.from('family_chat').insert({
      sender_token: token,
      message: msg,
    });

    if (error) console.error('Send error:', error);
  }, [text, token]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendText();
    }
  };

  // ── Photo upload ──────────────────────────────────────────────────────────
  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';

      setUploading(true);
      const ext = file.name.split('.').pop();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from('family-chat-media')
        .upload(path, file, { contentType: file.type });

      if (upErr) {
        console.error('Upload error:', upErr);
        setUploading(false);
        return;
      }

      const { data } = supabase.storage
        .from('family-chat-media')
        .getPublicUrl(path);

      const { error: insertErr } = await supabase.from('family_chat').insert({
        sender_token: token,
        message: '',
        media_url: data.publicUrl,
      });

      if (insertErr) console.error('Insert error:', insertErr);
      setUploading(false);
    },
    [token]
  );

  return (
    <div style={S.root}>
      {/* Global CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #0f3460; border-radius: 4px; }
      `}</style>

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.headerTitle}>Family Chat</div>
          <div style={S.headerSub}>Signed in as {myName}</div>
        </div>
        <div style={S.online}>
          <div style={S.dot} />
          {connected ? 'Live' : 'Connecting…'}
        </div>
      </div>

      {/* Messages */}
      <div style={S.messages}>
        {messages.map((m) => {
          const isMine = m.sender_token === token;
          const senderName = getNameForToken(m.sender_token);
          return (
            <div key={m.id} style={S.bubble(isMine)}>
              {!isMine && <div style={S.bubbleName(false)}>{senderName}</div>}
              {m.media_url && (
                <img
                  src={m.media_url}
                  alt="shared"
                  style={S.bubbleImg}
                  loading="lazy"
                />
              )}
              {m.message && <div style={S.bubbleText}>{m.message}</div>}
              <div style={S.bubbleTime}>{formatTime(m.created_at)}</div>
            </div>
          );
        })}
        {uploading && <div style={S.uploading}>Uploading photo…</div>}
        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div style={S.inputBar}>
        {/* Photo upload */}
        <label style={S.uploadLabel} title="Share a photo">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </label>

        {/* Text input */}
        <textarea
          ref={textareaRef}
          style={S.textarea}
          rows={1}
          placeholder="Type a message…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            autoGrow(e.target);
          }}
          onKeyDown={handleKeyDown}
        />

        {/* Send button */}
        <button
          style={S.sendBtn(text.trim().length > 0)}
          onClick={sendText}
          disabled={!text.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
}
